import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
  active: boolean;
  duration?: number;
  particleCount?: number;
}

const COLORS = ['#FFD700', '#FF4444', '#4488FF', '#44BB44', '#AA44FF', '#FF66AA'];

interface Particle {
  id: number;
  color: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
}

function createParticles(count: number): Particle[] {
  return Array.from({ length: count }, (_, i) => {
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.8;
    const speed = 200 + Math.random() * 400;
    return {
      id: i,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      x: 0,
      y: 0,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 200,
      size: 6 + Math.random() * 8,
      rotation: Math.random() * 360,
    };
  });
}

export function Confetti({ active, duration = 2000, particleCount = 30 }: Props) {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (active) {
      setParticles(createParticles(particleCount));
      setVisible(true);
      const timer = setTimeout(() => setVisible(false), duration);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [active, duration, particleCount]);

  return (
    <AnimatePresence>
      {visible && (
        <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden">
          <div className="absolute left-1/2 top-1/2">
            {particles.map((p) => (
              <motion.div
                key={p.id}
                initial={{
                  x: 0,
                  y: 0,
                  scale: 1,
                  opacity: 1,
                  rotate: 0,
                }}
                animate={{
                  x: p.vx,
                  y: p.vy + 300,
                  scale: 0,
                  opacity: 0,
                  rotate: p.rotation + 720,
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: duration / 1000,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                style={{
                  position: 'absolute',
                  width: p.size,
                  height: p.size,
                  borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                  backgroundColor: p.color,
                }}
              />
            ))}
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
